import { MouseEvent, useCallback, useMemo, useState } from 'react';
import { DocumentReferencesView } from 'api';
import {
  FORMAT_COLUMN_ID,
  NAME_COLUMN_ID,
  SENT_UPLOAD_DATE_COLUMN_ID,
  SOURCE_COLUMN_ID,
} from 'common/constants/documents';
import { Order } from 'components/EnhancedTableHead/types';
import { ExtendedFolderView } from '../CurrentRequestedDocumentsAndQuestions/types';

interface UseSortDocumentsProps {
  folders?: ExtendedFolderView[];
  files?: DocumentReferencesView[];
}

interface UseSortDocumentsReturn {
  sortDirection: Order;
  orderBy: string;
  handleRequestSort: (event: MouseEvent<unknown>, property: string) => void;
  sortedFolders: ExtendedFolderView[];
  sortedFiles: DocumentReferencesView[];
}

export const useSortDocuments = ({ folders, files }: UseSortDocumentsProps): UseSortDocumentsReturn => {
  const [sortDirection, setSortDirection] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<string>(SENT_UPLOAD_DATE_COLUMN_ID);

  const handleRequestSort = useCallback((_event: MouseEvent<unknown>, property: string) => {
    const isAsc = orderBy === property && sortDirection === 'asc';
    setSortDirection(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  }, [orderBy, sortDirection]);

  const getFileValue = useCallback((file: DocumentReferencesView, property: string): string | number => {
    switch (property) {
      case FORMAT_COLUMN_ID:
        return file.file.file_type || '';
      case NAME_COLUMN_ID:
        return file.file.filename || '';
      case SOURCE_COLUMN_ID:
        return file.file.uploaded_by?.full_name || '';
      case SENT_UPLOAD_DATE_COLUMN_ID:
        return file.file.uploaded_date ? new Date(file.file.uploaded_date).getTime() : 0;
      default:
        return '';
    }
  }, []);

  const compareFiles = useCallback((a: DocumentReferencesView, b: DocumentReferencesView, sortBy: string, sortOrder: Order): number => {
    const aValue = getFileValue(a, sortBy);
    const bValue = getFileValue(b, sortBy);

    if (bValue < aValue) {
      return sortOrder === 'desc' ? -1 : 1;
    }
    if (bValue > aValue) {
      return sortOrder === 'desc' ? 1 : -1;
    }
    return 0;
  }, [getFileValue]);

  const sortFiles = useCallback((filesToSort: DocumentReferencesView[]): DocumentReferencesView[] => 
    [...filesToSort].sort((a, b) => compareFiles(a, b, orderBy, sortDirection))
  , [compareFiles, orderBy, sortDirection]);

  const sortFoldersRecursive = useCallback((foldersToSort: ExtendedFolderView[]): ExtendedFolderView[] => 
    foldersToSort
      .map(folder => ({
        ...folder,
        files: sortFiles(folder.files || []),
        childFolders: sortFoldersRecursive(folder.childFolders || []),
      }))
      .sort((a, b) => {
        // For folders, we can only sort by name
        if (orderBy === NAME_COLUMN_ID) {
          const comparison = a.name.localeCompare(b.name);
          return sortDirection === 'desc' ? -comparison : comparison;
        }
        // For other columns, maintain original order (folders don't have those properties)
        return 0;
      })
  , [sortFiles, orderBy, sortDirection]);

  const sortedFolders = useMemo(() => {
    if (!folders) {
      return [];
    }
    return sortFoldersRecursive([...folders]);
  }, [folders, sortFoldersRecursive]);

  const sortedFiles = useMemo(() => {
    if (!files) {
      return [];
    }
    return sortFiles([...files]);
  }, [files, sortFiles]);

  return {
    sortDirection,
    orderBy,
    handleRequestSort,
    sortedFolders,
    sortedFiles,
  };
};
